import React, { useState, useEffect, useContext } from 'react';
import { routes, AuthorContext, useLocalStorage, PageContext } from 'gatsby-theme-apollo';
import moment from 'moment';
import styled from '@emotion/styled';
import { postTip } from '../../services/cms';
import { TextForm } from '../text/text-form';
import { TipContent, TipAuthor, TipDate, TipText } from '../text/user-text';
import { ContentBox } from '../layout/content-box';

const CTABanner = styled.div`
  width: 100%;
  text-align: center;
  padding: 30px 15px;
  outline-offset: -15px;
  transition: padding 0.33s ease;
  font-size: 24px;
  border-radius: ${props => props.theme.radius};
`;

const CTABannerTitle = styled.div`
  color: white;
  font-weight: bold;
  display: inline-block;
  padding: 2px 15px;
  margin-bottom: ${props => (props.small ? `0px` : `10px`)};
`;

const CTABannerSubtitle = styled.p`
  margin: 0px;
`;

const Tip = ({ text, createdAt, author, inReview }) => {
  return (
    <ContentBox>
      <TipContent>
        <TipText>{text}</TipText>
        {author && (
          <TipAuthor to={routes.User(author.name)}>
            by{` `}
            {author.name}
          </TipAuthor>
        )}
        <TipDate>
          {inReview ? 'Waiting for GamerHub Staff review' : moment(createdAt).fromNow()}
        </TipDate>
      </TipContent>
    </ContentBox>
  );
};

const Tips = ({ entity, tips }) => {
  const { author } = useContext(AuthorContext);
  const { logEvent } = useContext(PageContext);
  const [tipText, setTipText] = useLocalStorage(`${entity.name}-tip-text`);
  const [submittedTips, setSubmittedTips] = useLocalStorage(`${entity.name}-submitted-tips`, []);
  const [intentToSubmit, setIntentToSubmit] = useLocalStorage(
    `${entity.name}-tip-submit-intent`,
    false
  );
  const [submittedTipInSession, setSubmittedTipInSession] = useState(false);

  const SubmitTip = async ({ text }) => {
    if (!author || !entity.id) {
      console.warn(
        `Unable to submit tip for author: ${author.id} with text: ${text} on entity: ${entity.name}`
      );
      return;
    }
    if (!text) return;
    if (submittedTips && submittedTips.find(tip => tip.text === text)) return;

    logEvent('submit_tip', {
      text,
      entityName: entity && entity.name
    });

    const newTip = { authorID: author.id, text, entityID: entity.id };
    setSubmittedTips(submittedTips.concat(newTip));
    setSubmittedTipInSession(true);
    setIntentToSubmit(false);
    setTipText('');
    await postTip(newTip);
  };

  useEffect(() => {
    if (author) {
      if (intentToSubmit) {
        SubmitTip({ text: tipText });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [author, intentToSubmit, tipText]);

  return (
    <>
      {tips &&
        tips.map(tip => {
          return <Tip text={tip.text} author={tip.author} createdAt={tip.createdAt} />;
        })}

      {submittedTips &&
        submittedTips
          .filter(
            submitted =>
              !tips.find(publishedTip => {
                return publishedTip.text === submitted.text;
              })
          )
          .map(tip => {
            return <Tip text={tip.text} author={author} inReview />;
          })}

      <ContentBox style={{ marginTop: `10px`, padding: `10px`, flexWrap: `wrap` }}>
        <CTABanner style={{ width: `100%`, marginBottom: `15px` }} small={submittedTipInSession}>
          <CTABannerTitle small={submittedTipInSession}>
            {!submittedTipInSession ? `Got ${entity.name} tips?` : `You're awesome!`}
          </CTABannerTitle>

          <CTABannerSubtitle>
            {!submittedTipInSession
              ? `Share your strategies and techniques with the GamerHub community, and get recognized! 👏`
              : `Check back soon to see your tip added to GamerHub. In the meantime, you can submit more tips!`}
          </CTABannerSubtitle>
        </CTABanner>
        <TextForm
          contentType="Tip"
          onSignIn={() => {
            setIntentToSubmit(true);
          }}
          placeholder={`Type your ${entity.name} Tip`}
          onChange={newValue => {
            setTipText(newValue);
          }}
          onSubmit={() => {
            SubmitTip({ text: tipText });
          }}
          value={tipText}
        />
      </ContentBox>
    </>
  );
};

export default Tips;
